/**
 * Session-replay recorder — built to public/mdb-replay.js (esbuild IIFE, like the
 * checkout island). Loaded lazily (after `load`, never LCP-relevant) on funnel
 * pages, gated by the SAME consent as Mouseflow (_ms_consent / _ms_gdpr) and by a
 * per-session sample. Records with rrweb (maskAllInputs — keystroke VALUES never
 * leave the browser; Stripe/PayPal are cross-origin iframes rrweb can't see into),
 * gzips chunks (CompressionStream), and relays them through the Go API to R2.
 *
 * Spec: docs/specs/2026-07-14-lp-session-replay-heatmaps.md (R3). Everything is
 * wrapped so a recorder fault can NEVER degrade the page (money path) — on any
 * error the recorder stops silently.
 */
import { record } from "@rrweb/record";
import {
  decideRecording,
  consentAllowed,
  sanitizeHref,
  REC_COOKIE,
  type ReplayConfig,
} from "./gate";

// Caps mirror the server (spec S-D4). The server is the source of truth; these
// stop the client early so it doesn't waste a rejected upload.
const MAX_CHUNKS = 200;
const MAX_TOTAL_BYTES = 8 * 1024 * 1024;
const MAX_CHUNK_BYTES = 512 * 1024;
const FLUSH_MS = 10_000;
const FIRST_FLUSH_MS = 2_500; // flush the initial snapshot early so a bounce is still playable

function boot() {
  try {
    const cfg = (window as unknown as { __MDB_REPLAY__?: ReplayConfig }).__MDB_REPLAY__;
    if (!cfg || !cfg.apiUrl) return;
    if (typeof (window as unknown as { CompressionStream?: unknown }).CompressionStream !== "function") return; // no gzip → don't record (keeps the player simple)

    const getCookie = (n: string): string => {
      const m = document.cookie.match(new RegExp("(?:^|; )" + n + "=([^;]*)"));
      return m ? decodeURIComponent(m[1]) : "";
    };
    const setCookie = (n: string, v: string) => {
      try {
        // Only the sample decision + originating LP slug are set here (_mdb_rec /
        // _mdb_rec_slug). A 2-day max-age (not a pure session cookie) keeps a buyer
        // who returns to finish checkout the next day linked to the SAME LP session
        // in the recorder — while _lp_vid (1yr) preserves the visitor identity.
        document.cookie =
          n + "=" + encodeURIComponent(v) + ";path=/;SameSite=Lax;max-age=172800";
      } catch {
        /* ignore */
      }
    };

    // --- Which slug to post under, and whether to record at all (pure gate) ---
    const decision = decideRecording(cfg, getCookie);
    for (const ck of decision.cookiesToSet) setCookie(ck.name, ck.value);
    if (!decision.record || !decision.postSlug) return;
    const postSlug = decision.postSlug;

    // --- Consent gate (exactly the Mouseflow gate; see gate.ts consentAllowed) ---
    let started = false;
    const start = () => {
      if (started) return;
      if (!consentAllowed(getCookie, cfg.gdpr)) return;
      started = true;
      try {
        beginRecording(cfg, postSlug);
      } catch {
        /* recorder never breaks the page */
      }
    };

    if (!consentAllowed(getCookie, cfg.gdpr)) {
      // Join the shared grant registry (lib/consent-region.ts) so accepting the
      // banner also starts us; plus a short cookie poll as a fallback.
      const w = window as unknown as { __msGrantFns?: Array<() => void> };
      (w.__msGrantFns = w.__msGrantFns || []).push(start);
      let polls = 0;
      const iv = setInterval(() => {
        polls++;
        if (consentAllowed(getCookie, cfg.gdpr)) {
          clearInterval(iv);
          start();
        } else if (polls > 30) {
          clearInterval(iv); // ~60s: give up quietly
        }
      }, 2_000);
    } else {
      start();
    }
  } catch {
    /* never throw out of the recorder bootstrap */
  }
}

// Never LCP-relevant: run only after the load event (or immediately if the page
// already finished loading by the time this deferred asset executes).
if (document.readyState === "complete") {
  boot();
} else {
  window.addEventListener("load", boot, { once: true });
}

function beginRecording(cfg: ReplayConfig, postSlug: string) {
  const recordingId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "";
  if (!recordingId) return; // no UUID → the server rejects; don't bother

  // Website pages post to the site ingest (host+path surface key); landing pages
  // keep the slug endpoint untouched. The query params below are identical for
  // both — only the base and its surface key differ.
  const endpointBase = cfg.site
    ? `${cfg.apiUrl}/api/v1/site/replay?host=${encodeURIComponent(cfg.site.host)}&path=${encodeURIComponent(cfg.site.path)}`
    : `${cfg.apiUrl}/api/v1/lp/${encodeURIComponent(postSlug)}/replay`;
  const endpointSep = cfg.site ? "&" : "?";
  const vid = (() => {
    const m = document.cookie.match(/(?:^|; )_lp_vid=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : "";
  })();

  // page_path = pathname + a PII-SAFE subset of the query (utm_* + marketing
  // click-ids + creative). This distinguishes two visits to the same LP path
  // (?creative=fund-handles vs ?creative=fund-interfacing) in the session's
  // page-switcher. Everything else (email, _cid, unknown params) is dropped —
  // an ALLOWLIST so no PII can reach the stored page_path. Computed once.
  const pagePath = (() => {
    try {
      const keepExact: Record<string, true> = {
        creative: true, gclid: true, fbclid: true, ttclid: true,
        epik: true, wbraid: true, gbraid: true,
      };
      const src = new URLSearchParams(location.search);
      const out = new URLSearchParams();
      src.forEach((v, k) => {
        if (k.indexOf("utm_") === 0 || keepExact[k]) out.set(k, v);
      });
      const q = out.toString();
      return (location.pathname + (q ? "?" + q : "")).slice(0, 300);
    } catch {
      return location.pathname.slice(0, 300);
    }
  })();

  let buffer: unknown[] = [];
  let seq = 0;
  let totalBytes = 0;
  let fails = 0; // consecutive upload failures
  const MAX_FAILS = 3;
  const startedAt = Date.now();
  let stopped = false;
  let stopFn: (() => void) | null = null;
  let flushing = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    try {
      if (stopFn) stopFn();
    } catch {
      /* ignore */
    }
    clearInterval(interval);
  };

  const buildURL = (n: number, beacon: boolean): string => {
    const p = new URLSearchParams();
    p.set("rid", recordingId);
    p.set("seq", String(n));
    if (vid) p.set("vid", vid);
    p.set("path", pagePath);
    p.set("dur", String(Date.now() - startedAt));
    // Screen resolution for the recordings list (device column). Sent every
    // chunk; the server keeps the first-chunk value.
    try {
      if (screen && screen.width) p.set("sw", String(screen.width));
      if (screen && screen.height) p.set("sh", String(screen.height));
    } catch {
      /* ignore */
    }
    if ((window as unknown as { __mdbFriction?: boolean }).__mdbFriction) p.set("fric", "1");
    void beacon;
    return `${endpointBase}${endpointSep}${p.toString()}`;
  };

  const gzip = async (text: string): Promise<Uint8Array> => {
    const cs = new (window as unknown as { CompressionStream: new (f: string) => unknown }).CompressionStream("gzip") as {
      readable: ReadableStream<Uint8Array>;
      writable: WritableStream<Uint8Array>;
    };
    const writer = cs.writable.getWriter();
    void writer.write(new TextEncoder().encode(text));
    void writer.close();
    const reader = cs.readable.getReader();
    const parts: Uint8Array[] = [];
    let len = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        parts.push(value);
        len += value.length;
      }
    }
    const out = new Uint8Array(len);
    let off = 0;
    for (const part of parts) {
      out.set(part, off);
      off += part.length;
    }
    return out;
  };

  // flush drains the buffer into one gzipped chunk and uploads it. beacon=true
  // (pagehide) uses sendBeacon (fire-and-forget, no preflight); else fetch so we
  // can react to failure. Caps stop the recorder silently.
  const flush = async (beacon: boolean) => {
    if (stopped || flushing || buffer.length === 0) return;
    if (seq >= MAX_CHUNKS || totalBytes >= MAX_TOTAL_BYTES) {
      stop();
      return;
    }
    flushing = true;
    const batch = buffer;
    buffer = [];
    try {
      const gz = await gzip(JSON.stringify(batch));
      if (gz.length > MAX_CHUNK_BYTES) {
        stop(); // a single chunk over the per-chunk cap → give up (rare; big DOM burst)
        return;
      }
      const url = buildURL(seq, beacon);
      // Wrap once — a Blob is a valid body for both sendBeacon and fetch, and
      // text/plain keeps a beacon a CORS-simple request (reliable on unload).
      const blob = new Blob([gz as BlobPart], { type: "text/plain" });
      if (beacon && navigator.sendBeacon) {
        navigator.sendBeacon(url, blob);
        seq++;
        totalBytes += gz.length;
      } else {
        // NO keepalive here: the Fetch spec caps a keepalive request body at 64 KB,
        // and the initial DOM-snapshot chunk routinely exceeds that — a keepalive
        // fetch would reject it and the recording could never upload. Normal
        // fetch has no such cap; the pagehide flush uses sendBeacon instead.
        const res = await fetch(url, { method: "POST", body: blob, mode: "cors" });
        if (res.ok) {
          seq++;
          totalBytes += gz.length;
          fails = 0;
        } else {
          // Re-queue for one retry; give up after MAX_FAILS consecutive failures
          // or on a terminal status (spec §7 — never a retry storm on checkout).
          buffer = batch.concat(buffer);
          fails++;
          if (fails >= MAX_FAILS || res.status === 413 || res.status === 429 || res.status === 503) stop();
        }
      }
    } catch {
      // Put the batch back for one more try; a persistent failure stops via MAX_FAILS.
      buffer = batch.concat(buffer);
      fails++;
      if (fails >= MAX_FAILS) stop();
    } finally {
      flushing = false;
    }
  };

  // rrweb: mask everything typed; only [data-private] text is masked beyond that.
  // Keystroke VALUES never reach us; card fields live in cross-origin iframes.
  try {
    stopFn =
      record({
        emit(event: unknown) {
          if (stopped) return;
          // Strip the query/fragment from the rrweb Meta event's href (type 4).
          // rrweb records location.href verbatim, and maskAllInputs never touches
          // URLs — so a checkout URL like ?email=buyer@x.com would otherwise land
          // in the stored chunk. The path alone is enough context for playback.
          try {
            const e = event as { type?: number; data?: { href?: string } };
            if (e && e.type === 4 && e.data && typeof e.data.href === "string") {
              e.data.href = sanitizeHref(e.data.href);
            }
          } catch {
            /* never break the emit path */
          }
          buffer.push(event);
        },
        maskAllInputs: true,
        maskTextSelector: "[data-private]",
        recordCanvas: false,
        collectFonts: false,
        sampling: { mousemove: 50, scroll: 150, media: 800, input: "last" },
      }) || null;
  } catch {
    return; // rrweb failed to start — page unaffected
  }

  const interval = setInterval(() => {
    void flush(false);
  }, FLUSH_MS);
  setTimeout(() => void flush(false), FIRST_FLUSH_MS);

  const onHide = () => {
    void flush(true);
  };
  window.addEventListener("pagehide", onHide);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush(true);
  });
}
